import { useCallback, useEffect, useRef, useState } from "react";

import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { useDispatch } from "react-redux";

import { SaveFileRequest, TaskResultResponse } from "@/api/generated/types.gen";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { DocumentCategory } from "@/constants/documents";
import { useSaveClassifiedFiles } from "@/hooks/mutations/use-bundle-splitter";
import { useTaskResult } from "@/hooks/queries/use-bundle-splitter";
import { mapToValidClassification } from "@/pages/ReportStudio/DocumentSorter/utils/classificationMapper";
import { getFriendlyErrorMessage, notifyError } from "@/utils/notification";

import { FilePreview } from "../FilePreview";
import type { BundleRowStatus } from "../store/useBundleUploads";
import { ManualClassificationDialog } from "./ManualClassification";

type ManualClassificationFile = {
  filename: string;
  classification: string;
  fileKey?: string;
  url?: string;
  case?: { case_number: string };
  fileId?: string;
  startPage?: number;
  endPage?: number;
};

type ResultPhaseProps = {
  file: File;
  taskId: string;
  caseNumber: string;
  onComplete: (needsReview: boolean) => void;
  onDismiss?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onStatusChange?: (status: BundleRowStatus) => void;
};

export const ResultPhase = ({
  file,
  taskId,
  caseNumber,
  onComplete,
  onDismiss,
  onBusyChange,
  onStatusChange,
}: ResultPhaseProps) => {
  const [filesForManualClassification, setFilesForManualClassification] = useState<
    ManualClassificationFile[]
  >([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Set for the entire duration of the dialog's own save (not just while
  // it's visibly open), so the two actions can't be triggered concurrently
  // if the dialog is dismissed early mid-save.
  const [isModalSaving, setIsModalSaving] = useState(false);
  const [isSaveComplete, setIsSaveComplete] = useState(false);
  const [isSaveFailed, setIsSaveFailed] = useState(false);
  // A save request in flight — including a Retry Save after a failure.
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    data: taskResult,
    isError: isResultError,
    isFetching: isResultFetching,
    refetch: refetchTaskResult,
  } = useTaskResult({
    task_id: taskId,
    enabled: true,
  });

  const { mutateAsync: saveClassifiedFiles } = useSaveClassifiedFiles();
  const hasSavedRef = useRef(false);
  const dispatch = useDispatch();

  const handleSaveFiles = useCallback(
    async (result: TaskResultResponse) => {
      if (!result.result || !result.result.split_blob_urls) {
        setErrorMessage("No split files found in the task result.");
        return;
      }

      setIsSaving(true);
      try {
        const rangeMap = new Map<string, { startPage: number; endPage: number }>();

        const files: SaveFileRequest[] = result.result.split_blob_urls.map(
          (item) => {
            const classification = mapToValidClassification(item.class);
            // MDRS-548: drop any query string before taking the last segment.
            // The server derives this same name when it saves the split
            // itself, and /save de-duplicates on (case, classification, name)
            // — so a URL arriving with a SAS would otherwise have the browser
            // name the file "x.pdf?sv=..." while the server named it "x.pdf",
            // the dedup would miss, and the case would list the document
            // twice. Stripping it is also just correct: a signature has no
            // business in a filename.
            const filenameFromUrl =
              item.url.split("?")[0].split("/").pop() ||
              `${item.class.replaceAll(" ", "_")}_${item.startPage}-${item.endPage}.pdf`;

            rangeMap.set(filenameFromUrl, {
              startPage: item.startPage,
              endPage: item.endPage,
            });

            return {
              classification,
              file_url: item.url,
              filename: filenameFromUrl,
              filenameDefault: result.result?.file,
            };
          }
        );

        setErrorMessage(null);
        const data = await saveClassifiedFiles({
          case_number: caseNumber,
          data: { files },
        });

        // MDRS-414: /save can report per-file failures (blob-copy or DB-write
        // errors) inside a normal 200 response — it never throws for these,
        // so they were previously dropped silently and reported as full
        // success. Any failure (whether every file failed or just some)
        // blocks the review flow and offers "Retry Save" instead of falling
        // through to a success/review render alongside the failure alert —
        // retrying is safe, since the backend skips files already saved for
        // this (case, classification, name) (MDRS-222 idempotency), so
        // re-running only re-attempts what actually failed.
        const failedFiles = data?.failed_files ?? [];
        const savedCount = data?.saved_files?.length ?? 0;

        if (failedFiles.length > 0) {
          const failedFilenames = failedFiles
            .map((failedFile) =>
              typeof failedFile.filename === "string" ? failedFile.filename : null
            )
            .filter((filename): filename is string => Boolean(filename))
            .join(", ");
          const namesSuffix = failedFilenames ? `: ${failedFilenames}` : "";

          setErrorMessage(
            savedCount === 0
              ? `Failed to save ${failedFiles.length} file(s)${namesSuffix}.`
              : `${failedFiles.length} of ${savedCount + failedFiles.length} file(s) failed to save${namesSuffix}. The rest saved successfully — retrying will only re-attempt the failed file(s).`
          );
          setIsSaveFailed(true);
          return;
        }

        let needsReview = false;
        if (data?.saved_files) {
          const manualClassificationFiles = data.saved_files
            .filter(
              (file) =>
                file.classification ===
                DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS
            )
            .map((file) => {
              const filename = String(file.filename || "");
              const range = rangeMap.get(filename);
              return {
                filename,
                name: filename,
                classification: String(file.classification || ""),
                fileKey:
                  typeof file.blob_path === "string" ? file.blob_path : undefined,
                fileId:
                  typeof file.file_id === "string"
                    ? file.file_id
                    : String(file.file_id),
                case: { case_number: caseNumber },
                startPage: range?.startPage,
                endPage: range?.endPage,
              };
            });
          setFilesForManualClassification(manualClassificationFiles);
          needsReview = manualClassificationFiles.length > 0;
        }

        setIsSaveComplete(true);
        setIsSaveFailed(false);
        onComplete(needsReview);
      } catch (error) {
        console.error("Error saving files:", error);
        notifyError(error, dispatch);
        setErrorMessage(getFriendlyErrorMessage(error));
        setIsSaveFailed(true);
      } finally {
        setIsSaving(false);
      }
    },
    [caseNumber, saveClassifiedFiles, dispatch, onComplete]
  );

  const handleKeepAllInMiscellaneous = useCallback(() => {
    // The files are already saved under their default (Miscellaneous)
    // classification — accepting that default has nothing left to persist,
    // so this just dismisses the review prompt.
    setFilesForManualClassification([]);
  }, []);

  useEffect(() => {
    if (taskResult && !hasSavedRef.current) {
      hasSavedRef.current = true;
      handleSaveFiles(taskResult);
    }
  }, [taskResult, handleSaveFiles]);

  // MDRS-550: the results fetch gave up (React Query retries once) and nothing
  // fetches it again on its own — offer Retry rather than "Loading Results"
  // for good. A retry in flight reads as fetching again.
  const hasFetchFailed = isResultError && !taskResult && !isResultFetching;
  const isFetching = !taskResult && !errorMessage && !hasFetchFailed;
  const isResolvingReview = isModalSaving;

  // "No split files" is the one error that leaves nothing to save.
  const hasNothingToSave = !!errorMessage && !isSaveFailed;
  // MDRS-550: busy while this browser holds split results it hasn't saved —
  // fetching, saving, retrying, or a failed save waiting on Retry Save. Also
  // busy while a review saves. A failed fetch (not being retried) or an empty
  // result isn't, so neither can hold the toast open.
  //
  // MDRS-548 (AP-12) made the server save these too, so closing a row no
  // longer loses them outright. Kept all the same: the save the user can see
  // should still finish where they can see it, and the manual-classification
  // review below only runs off this response.
  const isBusy =
    (!isSaveComplete && !hasFetchFailed && !hasNothingToSave) || isModalSaving;
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  // MDRS-550: the pop-up's per-stage counts. A results fetch that failed
  // counts as failed — this browser won't be saving it.
  //
  // It is no longer lost, though: since MDRS-548 (AP-12) the server saves the
  // split itself, and a reconcile pass picks up anything this tab didn't. The
  // status reflects what the user can see happening here, not the fate of the
  // documents.
  const status: BundleRowStatus =
    isSaving || isModalSaving
      ? "saving"
      : isSaveFailed || hasFetchFailed || hasNothingToSave
        ? "failed"
        : !isSaveComplete
          ? "saving"
          : filesForManualClassification.length > 0
            ? "needs-review"
            : "done";
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  // Discarding a failed save or fetch is an explicit per-row choice (not
  // mid-retry).
  const canDismiss = isSaveComplete || (isSaveFailed && !isSaving) || hasFetchFailed;

  return (
    <>
      <FilePreview
        file={file}
        isSuccess={isSaveComplete && !isSaveFailed}
        isFailed={isSaveFailed || hasFetchFailed}
        isLoading={!isSaveComplete && !isSaveFailed && !hasFetchFailed}
        onClear={canDismiss ? onDismiss : undefined}
      />

      {errorMessage && (
        <Alert variant="destructive" className="mb-3">
          <XCircle />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {isSaveComplete ? (
        filesForManualClassification.length > 0 ? (
          <div className="w-full space-y-4">
            <Alert variant="warning" className="max-w-lg">
              <AlertTriangle className="mt-0.5 text-warning" />
              <AlertDescription>
                We have mapped most of your documents. But{" "}
                {filesForManualClassification.length}{" "}
                {filesForManualClassification.length === 1
                  ? "document needs"
                  : "documents need"}{" "}
                classification. Please review before continuing.
              </AlertDescription>
            </Alert>

            <div className="mt-3 flex gap-2">
              <Button
                variant="simple-outline"
                className="flex-1"
                onClick={handleKeepAllInMiscellaneous}
                disabled={isDialogOpen || isResolvingReview}
              >
                Keep in Miscellaneous
              </Button>
              <Button
                variant="default"
                className="flex-1"
                onClick={() => setIsDialogOpen(true)}
                disabled={isResolvingReview}
              >
                Review Now
              </Button>
            </div>

            <ManualClassificationDialog
              files={filesForManualClassification}
              open={isDialogOpen}
              onOpenChange={setIsDialogOpen}
              onFileClassified={(fileId) => {
                setFilesForManualClassification((prevFiles) =>
                  prevFiles.filter((file) => file.fileId !== fileId)
                );
              }}
              caseNumber={caseNumber}
              onSavingChange={setIsModalSaving}
            />
          </div>
        ) : (
          <Alert variant="success">
            <CheckCircle2 />
            <AlertDescription>Files saved successfully</AlertDescription>
          </Alert>
        )
      ) : hasFetchFailed ? (
        <div className="space-y-3">
          <Alert variant="destructive">
            <XCircle />
            <AlertDescription>
              Couldn&apos;t load the split results, so these documents haven&apos;t
              been saved to the case yet.
            </AlertDescription>
          </Alert>
          <Button onClick={() => void refetchTaskResult()}>Retry</Button>
        </div>
      ) : errorMessage && taskResult ? (
        <Button
          onClick={() => {
            // Mirror the auto-trigger effect's own guard (true, not false):
            // this call already re-invokes handleSaveFiles directly, so the
            // effect must not see a "not yet saved" ref and fire its own
            // duplicate save if handleSaveFiles' identity happens to change
            // on the re-render this triggers.
            hasSavedRef.current = true;
            handleSaveFiles(taskResult);
          }}
        >
          Retry Save
        </Button>
      ) : (
        isFetching && (
          <div className="space-y-1.5">
            <Progress className="h-2" />
            <p className="text-center text-xs text-foreground-muted">
              Loading Results
            </p>
          </div>
        )
      )}
    </>
  );
};
